import classNames from 'classnames';
import { memo, useState, useEffect, useMemo, useRef } from 'react';

import type { RBACGroup } from '../../../types';
import { useListKeyboardNav } from '../hooks/useListKeyboardNav';

type Props = {
  groups: RBACGroup[] | undefined;
  onGroupClick: (group: RBACGroup) => void;
  enabled: boolean;
  // When the selected ACL is an on-prem writeback group it's managed in AD, so the
  // role-add is disabled with an explanatory placeholder instead of "Select an ACL".
  readOnly?: boolean;
  // Message shown below the input, e.g. when a duplicate add is blocked.
  warning?: string;
};

function ACLRoleSelect({ groups, onGroupClick, enabled, readOnly = false, warning }: Props) {
  const [value, setValue] = useState('');
  const [groupList, setGroupList] = useState<RBACGroup[]>(groups ?? []);

  const inputRef = useRef<HTMLInputElement>(null);
  // The daisyUI .dropdown reveals its content purely via CSS :focus-within, so we can't
  // both keep the input focused after a pick AND have the list stay closed unless we gate
  // whether the content is rendered at all. isOpen is that gate: it starts true (the list
  // is rendered but stays CSS-hidden until focus, as before) and is only force-closed after
  // a pick, staying closed until the next focus/keystroke.
  const [isOpen, setIsOpen] = useState(true);
  // Picking refocuses the input to keep it ready for the next entry; that refocus fires
  // onFocus, which would otherwise re-open the list we just closed. This one-shot flag
  // swallows exactly that programmatic refocus.
  const suppressOpenRef = useRef(false);

  useEffect(() => {
    setGroupList(
      (groups ?? []).filter((item) => item.displayName?.toLowerCase().includes(value.toLowerCase()))
    );
  }, [value, groups]);

  // Memoized so the keyboard-nav hook only resets when the results actually change.
  const sortedList = useMemo(
    () => [...groupList].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [groupList]
  );

  const handleClick = (item: RBACGroup) => {
    onGroupClick(item);
    setValue('');
    // Close the list but keep the input focused so the next role can be typed straight away.
    suppressOpenRef.current = true;
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const { activeIndex, setActiveIndex, handleKeyDown, listRef } = useListKeyboardNav(
    sortedList,
    handleClick,
    () => setIsOpen(false),
    () => setIsOpen(true)
  );

  // Select all text when input is focused, and open the list — unless this focus came from
  // the programmatic refocus after a pick.
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (suppressOpenRef.current) {
      suppressOpenRef.current = false;
      return;
    }
    e.target.select();
    setIsOpen(true);
  };

  const inputClassName = classNames('input input-bordered w-full focus:outline-none');

  return (
    <div>
      <div className="font-bold pb-2">Add Role group:</div>
      <div className="dropdown w-72">
        <input
          ref={inputRef}
          type="text"
          className={inputClassName}
          placeholder={
            readOnly
              ? 'Managed through Entra group'
              : enabled
                ? 'Search for a Role group'
                : 'Select an ACL first'
          }
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setIsOpen(true);
          }}
          tabIndex={0}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={!enabled || readOnly}
        />
        {!isOpen || sortedList.length === 0 ? (
          <div></div>
        ) : (
          <div className="w-72 dropdown-content border border-base-200 bg-base-100 top-14 max-h-[50rem] overflow-auto flex-col rounded-md">
            <ul className="menu menu-compact" ref={listRef}>
              {sortedList.map((item, index) => (
                <li
                  key={item.id}
                  className={classNames('border-b last:border-b-0 border-b-base-200 w-full', {
                    'bg-primary text-primary-content': index === activeIndex,
                  })}
                >
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => handleClick(item)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className="flex flex-col items-start gap-0 w-full text-left"
                  >
                    <div className="truncate">{item.displayName}</div>
                    {'description' in item && item.description && (
                      <div className="text-xs w-full truncate" title={item.description}>
                        {item.description}
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {warning && <div className="text-warning text-xs pt-1">{warning}</div>}
    </div>
  );
}

export default memo(ACLRoleSelect);
